import React, { useCallback, useEffect, useState } from 'react';

import { SmDrawer } from '@smiles/smiles-react-design-system';
import { MODAL_DEFAULT_SCREEN } from '@/config';

import { TVoucherList } from '@/context/PurchaseProvider';

import useEvent from '@/hooks/useEvent';
import useApi from '@/hooks/useApi';
import usePurchase from '@/hooks/usePurchaseData';

import { contentMapper } from '@/services/requests/Liferay/getContentMapper';
import { TParsedContentResult } from '@/services/requests/Liferay/types';
import { TVoucherItem } from '@/services/requests/Voucher/types';

import { isLogged } from '@/helpers/AuthenticationHelper/SessionTokenHelper';
import { numberToBrl } from '@/helpers/StringHelper/numberToBrl';
import { getInitialScreenHelper } from '@/helpers/GetInitialScreenHelper';

import { ScreenDashboard } from './Screen/Dashboard';
import { ScreenLoading } from './Screen/Loading';
import { VoucherList } from './Screen/VoucherList/VoucherList';
import { FinishOrder } from './Screen/FinishOrder';
import { Rules } from './Screen/Rules';
import { ChangingNumberMiles } from './Screen/ChangingNumberMiles';
import { Error } from './Screen/Error';
import { AirlineVoucherRules } from './Screen/AirlineVoucherRules';
import { ScreenSelectVoucherAerea } from './Screen/SelectVoucherAerea';

export type TScreens =
  | 'dashboard'
  | 'voucherlist'
  | 'finishOrder'
  | 'rules'
  | 'airlineVoucherRules'
  | 'dashboardVoucherAerea'
  | 'changingNumberMiles'
  | 'error'
  | 'loading';

export const STORAGE_PREFIX = 'smiles-react-b2c-modals';

export function Modal() {
  const { ApiModal, setTraceId } = useApi();
  const event = useEvent();
  const usePurchaseData = usePurchase();

  const {
    voucherList,
    currentVoucher,
    setChangeVoucher,
    getPurchaseValues,
    milesToPurchase,
    fetchVoucherList,
  } = usePurchaseData;

  const [screen, setScreen] = useState<TScreens>(MODAL_DEFAULT_SCREEN);
  const [prevScreen, setPrevScreen] = useState<TScreens[]>([]);
  const [contentData, setContentData] = useState<TParsedContentResult>();

  const handleClose = useCallback(() => {
    setScreen(MODAL_DEFAULT_SCREEN);
    setPrevScreen([]);
    event.close();
  }, [event]);

  const handleBack = useCallback(() => {
    setScreen(prevScreen[prevScreen.length - 1] || MODAL_DEFAULT_SCREEN);
    prevScreen.pop();
    setPrevScreen(prevScreen);
  }, [prevScreen]);

  const handleChangeScreen = useCallback(
    (newScreen: TScreens) => {
      setScreen(prev => {
        prevScreen.push(prev);
        setPrevScreen(prevScreen);
        return newScreen;
      });
    },
    [prevScreen],
  );

  const fetchData = useCallback(async () => {
    fetchVoucherList();
    const result = await contentMapper(ApiModal, [
      'miles_purchase_voucher_select_voucher',
      'miles_purchase_voucher_summary',
      'miles_purchase_voucher_ruler',
      'miles_purchase_voucher_rules',
      'miles_purchase_voucher_value',
      'miles_purchase_voucher_error',
      'rcs_modal_airline_voucher_rules',
      'rcs_modal_select_voucher_type',
    ]);

    setContentData(result);
  }, [ApiModal, fetchVoucherList]);

  useEffect(() => {
    setTraceId(event.data.traceId);
  }, [event, setTraceId]);

  useEffect(() => {
    if (event.isOpen && (!contentData || !voucherList)) {
      if (!isLogged()) {
        const loginUrl = '/login?ref=/group/guest/minha-conta/minha-categoria';
        window.location.href = loginUrl;
      } else {
        fetchData();
      }
    }
  }, [
    contentData,
    event.data.voucherNumber,
    event.isOpen,
    fetchData,
    voucherList,
  ]);

  useEffect(() => {
    if (
      voucherList.all.length > 0 &&
      screen === MODAL_DEFAULT_SCREEN &&
      event.isOpen
    ) {
      const initialScreen = getInitialScreenHelper(
        voucherList,
        event.data.voucherNumber,
        event.data.modal,
        contentData,
      );

      setScreen(initialScreen.screen);
      setChangeVoucher(initialScreen.voucherNumber);
    }
  }, [
    event,
    setChangeVoucher,
    voucherList,
    currentVoucher,
    screen,
    contentData,
  ]);

  return (
    <>
      <SmDrawer
        className="smls-b2c-modals__modal"
        isOpen={event.isOpen}
        onClose={handleClose}
      >
        {event.isOpen && (
          <RenderContent
            screen={screen}
            contentData={contentData}
            handleClose={handleClose}
            voucherList={voucherList}
            handleBack={handleBack}
            changeScreen={handleChangeScreen}
            setChangeVoucher={setChangeVoucher}
            currentVoucher={currentVoucher}
            milesToPurchase={milesToPurchase}
            getPurchaseValues={getPurchaseValues}
          />
        )}
      </SmDrawer>
    </>
  );
}

const RenderContent: React.FC<{
  screen: TScreens;
  contentData: TParsedContentResult | undefined;
  handleClose: () => void;
  voucherList: TVoucherList;
  handleBack: () => void;
  changeScreen: (newScreen: TScreens) => void;
  setChangeVoucher: (voucherNumber: string) => void;
  currentVoucher: TVoucherItem | undefined;
  milesToPurchase: number;
  getPurchaseValues: (inputValue: number) => {
    installments: number;
    total: number;
    installmentValue: number;
    totalWithoutDiscount: number;
    id: string;
    mileValueWithDiscount: number;
    mileValue: number;
    expirationDate: string;
  };
}> = ({
  screen,
  contentData,
  handleClose,
  voucherList,
  handleBack,
  changeScreen,
  setChangeVoucher,
  currentVoucher,
  milesToPurchase,
  getPurchaseValues,
}) => {
  if (!contentData || !currentVoucher || screen === 'loading') {
    return <ScreenLoading onClose={handleClose} />;
  }

  const screens = {
    dashboard: (
      <ScreenDashboard
        onClose={handleClose}
        liferayContent={contentData.miles_purchase_voucher_ruler}
        changeScreen={changeScreen}
      />
    ),
    dashboardVoucherAerea: (
      <ScreenSelectVoucherAerea
        onClose={handleClose}
        liferayContent={contentData.rcs_modal_select_voucher_type}
        changeScreen={changeScreen}
      />
    ),
    voucherlist: (
      <VoucherList
        onBack={handleBack}
        liferayContent={contentData.miles_purchase_voucher_select_voucher}
        voucherList={voucherList.milesPurchase}
        setChangeVoucher={setChangeVoucher}
        changeScreen={changeScreen}
      />
    ),
    airlineVoucherRules: (
      <AirlineVoucherRules
        onClose={handleClose}
        liferayContent={contentData.rcs_modal_airline_voucher_rules}
        currentVoucher={currentVoucher}
      />
    ),
    rules: (
      <Rules
        onClose={handleClose}
        onBack={handleBack}
        liferayContent={contentData.miles_purchase_voucher_rules}
        currentVoucher={currentVoucher}
        purchaseValues={getPurchaseValues(milesToPurchase)}
      />
    ),
    finishOrder: (
      <FinishOrder
        onBack={handleBack}
        liferayContent={contentData.miles_purchase_voucher_summary}
        totalSelected={milesToPurchase}
        voucherLimit={currentVoucher?.availableMiles || 0}
        milesValueTotal={numberToBrl(
          getPurchaseValues(milesToPurchase).totalWithoutDiscount || 0,
        )}
        totalDiscount={numberToBrl(
          getPurchaseValues(milesToPurchase).totalWithoutDiscount -
            getPurchaseValues(milesToPurchase).total || 0,
        )}
        totalToPay={numberToBrl(getPurchaseValues(milesToPurchase).total || 0)}
        changeScreen={changeScreen}
        currentVoucher={currentVoucher}
        purchaseId={getPurchaseValues(milesToPurchase).id || ''}
        onClose={handleClose}
        voucherListLength={voucherList.milesPurchase.length}
      />
    ),
    changingNumberMiles: (
      <ChangingNumberMiles
        onBack={handleBack}
        liferayContent={contentData.miles_purchase_voucher_value}
        changeScreen={changeScreen}
      />
    ),
    error: (
      <Error
        liferayContent={contentData.miles_purchase_voucher_error}
        changeScreen={changeScreen}
        onClose={handleClose}
      />
    ),
  };
  return screens[screen];
};
